import { useMemo, useState } from "react";

import { formatPrice } from "utils/formats";

import {
  Channel,
  computeTaxaHeatmap,
  filterByChannel,
  HeatSourceRow,
  isLinkPayment,
} from "../../aggregations/taxaHeatmap";
import * as S from "../../styles";

import * as H from "./styles";

type Metric = "count" | "value";

interface Props {
  rows: HeatSourceRow[];
  isLoading: boolean;
}

const CHANNELS: { key: Channel; label: string; icon: string }[] = [
  { key: "all", label: "Todos", icon: "uil-layer-group" },
  { key: "maquina", label: "Máquina física", icon: "uil-credit-card" },
  { key: "link", label: "Link de pagamento", icon: "uil-link" },
];

const fmtTaxa = (t: number) =>
  `${t.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// Valor compacto para caber na célula (ex.: R$ 45,3 mil). Título traz o valor cheio.
const fmtCompact = (n: number) => {
  if (n >= 1000000) return `${(n / 1000000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}M`;
  if (n >= 1000) return `${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
};

const TaxaHeatmapCard = ({ rows, isLoading }: Props) => {
  const [metric, setMetric] = useState<Metric>("count");
  const [channel, setChannel] = useState<Channel>("all");

  // Filtra por canal (paymentCode = Link; sem = Máquina física) e monta a matriz.
  const data = useMemo(
    () => computeTaxaHeatmap(filterByChannel(rows, channel)),
    [rows, channel]
  );
  const channelCounts = useMemo(() => {
    let link = 0;
    for (const r of rows) if (isLinkPayment(r)) link += 1;
    return { all: rows.length, link, maquina: rows.length - link };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="d-flex flex-column gap-2" aria-label="Carregando mapa de calor">
        <S.Skeleton height="2rem" width="60%" />
        <S.Skeleton height="16rem" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-muted text-center py-5">
        <i className="uil uil-apps display-4 d-block mb-2 opacity-25" aria-hidden="true"></i>
        <small className="d-block">Nenhum orçamento concluído no período.</small>
        <small>Ajuste o período ou os filtros acima.</small>
      </div>
    );
  }

  const isCount = metric === "count";
  const max = isCount ? data.maxCount : data.maxValue;
  const cellRaw = (pi: number, ti: number) => (isCount ? data.count[pi][ti] : data.value[pi][ti]);
  const rowTotal = (pi: number) =>
    isCount ? data.parcelaTotals[pi].count : data.parcelaTotals[pi].value;
  const colTotal = (ti: number) =>
    isCount ? data.taxaTotals[ti].count : data.taxaTotals[ti].value;
  const grandTotal = isCount ? data.totalCount : data.totalValueSum;

  // Ticket médio (valor ÷ contagem) — sempre em R$, independente da métrica.
  const overallTicket = data.totalCount > 0 ? data.totalValueSum / data.totalCount : 0;
  let maxTicket = 0;
  for (const pt of data.parcelaTotals) {
    const tk = pt.count > 0 ? pt.value / pt.count : 0;
    if (tk > maxTicket) maxTicket = tk;
  }

  const fmtPct = (n: number) =>
    `${n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

  const renderNumber = (n: number, filled: boolean) => {
    if (!filled) return <H.Empty aria-hidden="true">·</H.Empty>;
    return isCount ? n.toLocaleString("pt-BR") : fmtCompact(n);
  };

  const hasData = data.parcelas.length > 0 && data.taxas.length > 0;

  return (
    <div>
      {/* Canal: Todos / Máquina física / Link de pagamento */}
      <div
        className="btn-group btn-group-sm mb-3 flex-wrap"
        role="group"
        aria-label="Canal de pagamento"
      >
        {CHANNELS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`btn ${channel === c.key ? "btn-primary" : "btn-outline-primary"}`}
            aria-pressed={channel === c.key}
            onClick={() => setChannel(c.key)}
          >
            <i className={`uil ${c.icon} me-1`} aria-hidden="true"></i>
            {c.label}
            <span className="ms-1 opacity-75">({channelCounts[c.key].toLocaleString("pt-BR")})</span>
          </button>
        ))}
      </div>

      {/* Métrica + legenda */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Métrica do mapa de calor">
          <button
            type="button"
            className={`btn ${isCount ? "btn-primary" : "btn-outline-primary"}`}
            aria-pressed={isCount}
            onClick={() => setMetric("count")}
          >
            <i className="uil uil-apps me-1" aria-hidden="true"></i>
            Contagem
          </button>
          <button
            type="button"
            className={`btn ${!isCount ? "btn-primary" : "btn-outline-primary"}`}
            aria-pressed={!isCount}
            onClick={() => setMetric("value")}
          >
            <i className="uil uil-money-bill me-1" aria-hidden="true"></i>
            Valor transacionado
          </button>
        </div>

        <div className="d-flex align-items-center gap-2 text-muted small">
          <span>Menos usado</span>
          <H.LegendBar aria-hidden="true" />
          <span>Mais usado</span>
        </div>
      </div>

      {!hasData ? (
        <div className="text-muted text-center py-5">
          <i className="uil uil-filter-slash display-4 d-block mb-2 opacity-25" aria-hidden="true"></i>
          <small className="d-block">Nenhuma transação neste canal no período.</small>
          <small>Selecione outro canal acima.</small>
        </div>
      ) : (
        <>
          {/* Taxas mais usadas */}
          <ul className="list-unstyled d-flex flex-wrap gap-2 mb-3" aria-label="Taxas mais usadas">
            {data.topTaxas.map((t) => (
              <H.Chip key={t.taxa}>
                <b>{fmtTaxa(t.taxa)}</b>
                <small>
                  {t.count.toLocaleString("pt-BR")}× · {formatPrice(t.value)}
                </small>
              </H.Chip>
            ))}
          </ul>

          <H.Scroll>
            <H.Table>
              <caption className="visually-hidden">
                Mapa de calor de {isCount ? "contagem de transações" : "valor transacionado"} por
                parcelas (linhas) e taxa (colunas).
              </caption>
              <thead>
                <tr>
                  <th scope="col">Parcelas \ Taxa</th>
                  {data.taxas.map((t) => (
                    <th key={t} scope="col">
                      {fmtTaxa(t)}
                    </th>
                  ))}
                  <th scope="col">Total</th>
                  <th scope="col">% do total</th>
                  <th scope="col">Ticket médio</th>
                </tr>
              </thead>
              <tbody>
                {data.parcelas.map((p, pi) => {
                  const rowT = rowTotal(pi);
                  const pct = grandTotal > 0 ? (rowT / grandTotal) * 100 : 0;
                  const rowCnt = data.parcelaTotals[pi].count;
                  const ticket = rowCnt > 0 ? data.parcelaTotals[pi].value / rowCnt : 0;
                  return (
                    <tr key={p}>
                      <th scope="row">{p}×</th>
                      {data.taxas.map((t, ti) => {
                        const raw = cellRaw(pi, ti);
                        const filled = raw > 0;
                        const intensity = filled && max > 0 ? raw / max : 0;
                        return (
                          <H.Cell
                            key={t}
                            $t={intensity}
                            $filled={filled}
                            title={
                              filled
                                ? `${p}× a ${fmtTaxa(t)} — ${data.count[pi][ti].toLocaleString(
                                    "pt-BR"
                                  )} transação(ões), ${formatPrice(data.value[pi][ti])}`
                                : undefined
                            }
                          >
                            {renderNumber(raw, filled)}
                          </H.Cell>
                        );
                      })}
                      <th scope="row" className="text-success">
                        {isCount ? rowT.toLocaleString("pt-BR") : formatPrice(rowT)}
                      </th>
                      <H.SummaryCell>
                        <H.CellNum>{fmtPct(pct)}</H.CellNum>
                        <H.Track>
                          <H.Fill $w={pct} $color="var(--primary)" />
                        </H.Track>
                      </H.SummaryCell>
                      <H.SummaryCell>
                        <H.CellNum>{formatPrice(ticket)}</H.CellNum>
                        <H.Track>
                          <H.Fill
                            $w={maxTicket > 0 ? (ticket / maxTicket) * 100 : 0}
                            $color="#3b82f6"
                          />
                        </H.Track>
                      </H.SummaryCell>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {data.taxas.map((t, ti) => (
                    <td key={t}>
                      {isCount ? colTotal(ti).toLocaleString("pt-BR") : fmtCompact(colTotal(ti))}
                    </td>
                  ))}
                  <th scope="row" className="text-success">
                    {isCount ? grandTotal.toLocaleString("pt-BR") : formatPrice(grandTotal)}
                  </th>
                  <th scope="row">{fmtPct(100)}</th>
                  <th scope="row">{formatPrice(overallTicket)}</th>
                </tr>
              </tfoot>
            </H.Table>
          </H.Scroll>
        </>
      )}

      <p className="small text-muted mt-2 mb-0">
        <i className="uil uil-info-circle me-1" aria-hidden="true"></i>
        Apenas orçamentos concluídos (repasse feito) no período. Canal:{" "}
        <b>Link de pagamento</b> quando há código de transação (NSU); caso contrário{" "}
        <b>Máquina física</b>. Taxa efetiva = (valor − compensação) / valor.
      </p>
    </div>
  );
};

export default TaxaHeatmapCard;
